/* ============================================================
 * N2T ship-week display + zip-prompt modal
 *
 * Reads/writes the customer's ZIP from localStorage so they only
 * enter it once per device. Computes "arrives the week of <Thursday>"
 * by back-solving from the customer's target arrival day and the
 * carrier transit time for their zone.
 *
 * Required globals (load order):
 *   1. ship-week-zones.js      → window.N2T_SHIP_ZONES
 *   2. ship-week-warehouse.js  → window.N2T_SHIP_WAREHOUSE
 *   3. ship-week.js            → this file
 *
 * Configuration via data-* attrs on <ship-week-display>:
 *   data-warehouse-zip   informational warehouse origin zip
 *   data-cutoff-day      target arrival day-of-week (0=Sun ... 4=Thu).
 *                        Despite the legacy name, this is the day boxes
 *                        ARRIVE on, not the day they ship.
 *
 * Storage:
 *   localStorage['n2t_ship_zip']  → user's ZIP (string)
 *
 * ──── Date math (the rule, set by ops 2026-05-02) ───────────
 *
 *   effective_order_date =
 *     today              if customer's current clock < 10:00 in
 *                        warehouse timezone (Americold 62071 = Central)
 *     today + 1 day      otherwise
 *
 *   IF effective_order_date + transit_days ≤ this week's target:
 *     → ship this week, arrival = this Thursday
 *   ELSE:
 *     → ship next week,  arrival = next Thursday
 *
 *   Repeat the +7 push for each blacked-out target Thursday until
 *   we find a feasible non-blackout one.
 * ============================================================ */
(function () {
  'use strict';

  const STORAGE_KEY = 'n2t_ship_zip';
  const DEFAULT_TARGET_ARRIVAL_DAY = 4; // Thursday
  const SAFETY_WEEKS = 6;               // upper bound on the back-solve loop

  function getStoredZip() {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function setStoredZip(zip) {
    try {
      localStorage.setItem(STORAGE_KEY, zip);
    } catch (e) {
      /* localStorage unavailable (private mode, full disk) — ignore */
    }
  }

  // ---- Lookup telemetry (issue #199) ----------------------------------
  // The estimator used to emit nothing, so a stale or broken transit map
  // failed silently. These events make the three outcomes visible:
  // resolved / not recognized / zones fetch failed.
  //
  // Matters especially after the 2026-08 FedEx rebuild: 178 ZIPs that the
  // old CC3PL/UPS map covered (176 Puerto Rico + two retired ZIPs) are not
  // in the FedEx Home Delivery map, so they now land on "not recognized".
  //
  // Posture: NEVER THROW. Analytics must never break the pill. Deduped per
  // (zip, outcome) because render() runs again after the lazy zones fetch
  // resolves — without the guard every shopper would emit twice.
  const _seenLookups = new Set();

  function trackLookup(outcome, zip, zone, region) {
    try {
      const key = outcome + ':' + (zip || '');
      if (_seenLookups.has(key)) return;
      _seenLookups.add(key);

      const p = (typeof window !== 'undefined' && window.posthog) || null;
      if (!p || typeof p.capture !== 'function') return;

      const props = { outcome: outcome, surface: 'pdp_pill' };
      if (region) props.region = region;
      if (zip) props.zip_prefix = String(zip).slice(0, 3); // ZIP3 — coarse, not PII-precise
      if (zone) {
        props.ground_transit_days = zone.ground_transit_days;
        props.transit_days = zone.transit_days;
        props.service = zone.service;
      }
      p.capture('ship_week_zip_lookup', props);
    } catch (e) {
      /* analytics must never break the storefront */
    }
  }

  function isoDate(d) {
    // Local-date ISO string (YYYY-MM-DD) — NOT UTC. Customer's clock.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  // Returns the hour-of-day (0-23) at the warehouse's location for a given
  // moment in time, regardless of customer's local timezone. Falls back to
  // customer-local hour if Intl is unavailable (very old browsers).
  function getWarehouseHour(date) {
    const cfg = (window.N2T_SHIP_WAREHOUSE) || {};
    const tz = cfg.origin_timezone || 'America/Chicago';
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: 'numeric',
        hour12: false,
      });
      const h = parseInt(formatter.format(date), 10);
      return Number.isFinite(h) ? h : date.getHours();
    } catch (e) {
      return date.getHours();
    }
  }

  // Compute the target arrival date and the latest feasible ship date
  // for an order placed `now`, given the customer's transit zone and the
  // warehouse's daily cutoff.
  //
  // Rule (set by ops 2026-05-02; origin re-based to Americold/FedEx 2026-08):
  //   effective_order_date + transit_days ≤ target_arrival_date
  //   where effective_order_date = today (if before warehouse 10am cutoff)
  //                              or today + 1 day (otherwise)
  //
  // Returns { ship_date, arrival_date } or null if no feasible date.
  function computeArrival(targetArrivalDay, transitDays, today) {
    const cfg = (window.N2T_SHIP_WAREHOUSE) || {};
    const cutoffHour = (typeof cfg.daily_cutoff_hour === 'number' && cfg.daily_cutoff_hour < 24)
      ? cfg.daily_cutoff_hour
      : 24;
    const blackouts = new Set(cfg.blackout_dates || []);

    const ref = today || new Date();

    // Anchor "today" to local midnight so day arithmetic is stable.
    const refMidnight = new Date(ref);
    refMidnight.setHours(0, 0, 0, 0);

    // effective_order_date: today if before warehouse cutoff, else today+1.
    const effectiveOrderDate = new Date(refMidnight);
    if (getWarehouseHour(ref) >= cutoffHour) {
      effectiveOrderDate.setDate(refMidnight.getDate() + 1);
    }

    // Earliest possible arrival from this order = effective_order_date + transit.
    const earliestArrival = new Date(effectiveOrderDate);
    earliestArrival.setDate(effectiveOrderDate.getDate() + transitDays);

    // Walk forward through candidate target arrival days (Thursdays by default)
    // until one satisfies BOTH: (a) earliestArrival ≤ candidate, (b) candidate
    // is not blacked out.
    const candidate = new Date(refMidnight);
    let daysAhead = (targetArrivalDay - candidate.getDay() + 7) % 7;
    if (daysAhead === 0) {
      // Today IS the target day. Per the rule we'd need effective_order_date +
      // transit ≤ today, which only works for transit=0 + before-cutoff.
      // For any transit ≥ 1 today is unreachable; advance to next week.
      // (Also matches the user's intent: don't promise "today" deliveries.)
      daysAhead = 7;
    }
    candidate.setDate(refMidnight.getDate() + daysAhead);

    for (let week = 0; week < SAFETY_WEEKS; week++) {
      const candidateIso = isoDate(candidate);
      const isBlacked = blackouts.has(candidateIso);
      const reachable = earliestArrival <= candidate;
      if (!isBlacked && reachable) {
        const ship = new Date(candidate);
        ship.setDate(candidate.getDate() - transitDays);
        return { ship_date: ship, arrival_date: new Date(candidate) };
      }
      candidate.setDate(candidate.getDate() + 7);
    }
    return null;
  }

  // Returns Monday of the calendar week containing `date`.
  // ISO 8601 convention: weeks run Monday → Sunday. Sunday (getDay() === 0)
  // is treated as the LAST day of the prior week.
  function weekMondayOf(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const offsetToMonday = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + offsetToMonday);
    return d;
  }

  // Returns Friday of the calendar week containing `date`.
  function weekFridayOf(date) {
    const monday = weekMondayOf(date);
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return friday;
  }

  // English ordinal suffix for a day-of-month (1-31). Handles the 11/12/13
  // trap explicitly: those use "th", not "st"/"nd"/"rd".
  function ordinalSuffix(day) {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  // "Monday, May 4th" — two toLocaleDateString calls (explicit over clever).
  function formatShipWeekLabel(date) {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month   = date.toLocaleDateString('en-US', { month: 'long' });
    const day     = date.getDate();
    return weekday + ', ' + month + ' ' + day + ordinalSuffix(day);
  }

  // Compute the customer-facing label dates for the ship-week pill.
  // ALWAYS uses arrival_date as the anchor — NOT ship_date. For cross-country
  // shipments where transit pushes ship_date back into the prior calendar
  // week (Sun for transit=4 + Thu target arrival), anchoring to ship_date
  // would underpromise the arrival by 1 week. Mirrors the same arrival-week
  // semantics used in assets/ship-week-cart.js (cart-stage component).
  function buildPillDates(result) {
    return {
      shipMonday:    weekMondayOf(result.arrival_date),
      arriveFriday:  weekFridayOf(result.arrival_date),
    };
  }

  function computeArrivalForZip(zip, targetArrivalDay, mockNow) {
    const zone = window.N2T_SHIP_ZONES && window.N2T_SHIP_ZONES.lookup(zip);
    if (!zone) return null;
    const result = computeArrival(targetArrivalDay, zone.transit_days, mockNow);
    if (!result) return null;
    return {
      date: result.arrival_date,
      arrival_date: result.arrival_date,
      ship_date: result.ship_date,
      zone,
    };
  }

  // Parse the merchant-provided mock date string. Accepts ISO-like
  // formats: "2026-05-05T11:00", "2026-05-05 11:00", "2026-05-05".
  // Returns null if empty or unparseable.
  function parseMockDate(s) {
    if (!s || typeof s !== 'string') return null;
    const trimmed = s.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed.replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
  }

  if (!customElements.get('ship-week-display')) {
    customElements.define(
      'ship-week-display',
      class ShipWeekDisplay extends HTMLElement {
        connectedCallback() {
          this.render();
          this.addEventListener('click', this.onClick.bind(this));
          window.addEventListener('n2t:ship-zip-changed', this.render.bind(this));

          // Zone data loads lazily (one fetch per page). render() above
          // shows the empty/CTA state synchronously; once the JSON resolves
          // we re-render so a stored ZIP can resolve to a real arrival pill.
          // No race: lookup() returns null until loadZones() populates the
          // cache, so an early invocation just falls into the "ZIP not
          // recognized" branch — which the post-load render() corrects.
          if (window.N2T_SHIP_ZONES && typeof window.N2T_SHIP_ZONES.loadZones === 'function') {
            const self = this;
            window.N2T_SHIP_ZONES.loadZones().then(function (data) {
              // loadZones() swallows fetch/parse errors and resolves to {}.
              // An empty map means the transit data never arrived — that is a
              // different failure from "this ZIP isn't served", and the pill
              // renders identically for both, so separate them here.
              if (!data || Object.keys(data).length === 0) {
                trackLookup('zones_unavailable', getStoredZip(), null);
              }
              self.render();
            });
          }
        }

        // Despite the legacy attribute name `data-cutoff-day`, this is the
        // target ARRIVAL day (the day customers' boxes should land), not
        // the day they ship. Ship date is back-solved from arrival - transit.
        get targetArrivalDay() {
          return parseInt(this.dataset.cutoffDay, 10) || DEFAULT_TARGET_ARRIVAL_DAY;
        }

        get mockNow() {
          return parseMockDate(this.dataset.testModeDate);
        }

        render() {
          const zip = getStoredZip();
          const mockNow = this.mockNow;
          if (!zip) {
            this.innerHTML = `
              ${mockNow ? renderTestBadge(mockNow) : ''}
              <button type="button" class="ship-week ship-week--empty" data-action="prompt">
                <span class="ship-week__icon" aria-hidden="true">📦</span>
                <span class="ship-week__cta">Add ZIP for delivery date →</span>
              </button>`;
            return;
          }
          const result = computeArrivalForZip(zip, this.targetArrivalDay, mockNow);
          const zonesReady = !!(window.N2T_SHIP_ZONES
            && typeof window.N2T_SHIP_ZONES.hasData === 'function'
            && window.N2T_SHIP_ZONES.hasData());
          // Only report an outcome once the map is actually loaded — the
          // pre-load render() always falls into the !result branch, and
          // reporting there would drown the real signal in false negatives.
          // Destinations the store cannot ship to at all (AK, HI, the
          // territories) deserve a straight answer, not "not recognized" —
          // that reads as a typo and sends the shopper round the loop.
          const unserved = (window.N2T_SHIP_ZONES
            && typeof window.N2T_SHIP_ZONES.unservedRegion === 'function')
            ? window.N2T_SHIP_ZONES.unservedRegion(zip)
            : null;
          if (unserved) {
            trackLookup('unserved', zip, null, unserved);
            this.innerHTML = `
              <button type="button" class="ship-week ship-week--invalid" data-action="prompt">
                <span class="ship-week__icon" aria-hidden="true">⚠️</span>
                <span class="ship-week__cta">We don't ship to ${escapeHtml(unserved)} yet — change ZIP</span>
              </button>`;
            return;
          }
          if (zonesReady) {
            trackLookup(result ? 'resolved' : 'not_recognized', zip, result && result.zone);
          }
          if (!result) {
            this.innerHTML = `
              <button type="button" class="ship-week ship-week--invalid" data-action="prompt">
                <span class="ship-week__icon" aria-hidden="true">⚠️</span>
                <span class="ship-week__cta">ZIP ${escapeHtml(zip)} not recognized — change</span>
              </button>`;
            return;
          }
          const dates        = buildPillDates(result);
          const shipLabel    = formatShipWeekLabel(dates.shipMonday);
          const arriveLabel  = formatShipWeekLabel(dates.arriveFriday);
          this.innerHTML = `
            ${mockNow ? renderTestBadge(mockNow) : ''}
            <div class="ship-week ship-week--filled">
              <span class="ship-week__icon" aria-hidden="true">📦</span>
              <span class="ship-week__copy">
                <span class="ship-week__line ship-week__line--ship">Ships week of ${escapeHtml(shipLabel)}</span>
                <span class="ship-week__line ship-week__line--arrive">Arrives on or before ${escapeHtml(arriveLabel)}</span>
              </span>
              <button type="button" class="ship-week__change" data-action="prompt" aria-label="Change ZIP code">
                ${escapeHtml(zip)} · change
              </button>
            </div>`;
        }

        onClick(e) {
          if (e.target.closest('[data-action="prompt"]')) {
            openModal();
            return;
          }
        }
      }
    );
  }

  function openModal() {
    const modal = document.querySelector('ship-week-modal');
    if (modal && typeof modal.open === 'function') modal.open();
  }

  if (!customElements.get('ship-week-modal')) {
    customElements.define(
      'ship-week-modal',
      class ShipWeekModal extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          this.form = this.querySelector('form');
          this.input = this.querySelector('input[name="zip"]');
          this.error = this.querySelector('.ship-week-modal__error');

          if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
          this.querySelectorAll('[data-action="close"]').forEach((b) =>
            b.addEventListener('click', this.close.bind(this))
          );
        }

        open() {
          if (!this.dialog) return;
          this.input.value = getStoredZip();
          this.error.textContent = '';
          if (typeof this.dialog.showModal === 'function') {
            this.dialog.showModal();
          } else {
            this.dialog.setAttribute('open', '');
          }
          requestAnimationFrame(() => this.input.focus());
        }

        close() {
          if (!this.dialog) return;
          if (typeof this.dialog.close === 'function') {
            this.dialog.close();
          } else {
            this.dialog.removeAttribute('open');
          }
        }

        onSubmit(e) {
          e.preventDefault();
          const zip = (this.input.value || '').trim();
          if (!window.N2T_SHIP_ZONES.isValidUSZip(zip)) {
            this.error.textContent = 'Please enter a 5-digit US ZIP code.';
            return;
          }
          // Await zones load before validating against the table — modal
          // can open before the lazy fetch resolves, and a sync lookup()
          // would mis-classify a known ZIP as unshippable.
          const self = this;
          function acceptAndClose() {
            setStoredZip(zip);
            window.dispatchEvent(new CustomEvent('n2t:ship-zip-changed', { detail: { zip } }));
            self.close();
          }
          const loader = (window.N2T_SHIP_ZONES && typeof window.N2T_SHIP_ZONES.loadZones === 'function')
            ? window.N2T_SHIP_ZONES.loadZones()
            : Promise.resolve();
          loader.then(function () {
            if (!window.N2T_SHIP_ZONES.lookup(zip)) {
              const region = (typeof window.N2T_SHIP_ZONES.unservedRegion === 'function')
                ? window.N2T_SHIP_ZONES.unservedRegion(zip)
                : null;
              // Name the place when we know it. "We don't ship to Alaska" is
              // an answer; "we don't ship to ZIP 99501" invites a retry.
              self.error.textContent = region
                ? "Sorry, we don't ship to " + region + ' yet.'
                : "Sorry, we don't currently ship to ZIP " + zip + '.';
              return;
            }
            acceptAndClose();
          }).catch(function () {
            // Never trap the shopper behind a stuck modal. If the zones load or
            // lookup throws, the ZIP already passed format validation — accept
            // it and close. render() classifies it afterwards; a "not
            // recognized" pill beats a dead page over add-to-cart.
            acceptAndClose();
          });
        }
      }
    );
  }

  function renderTestBadge(mockNow) {
    const niceDate = mockNow.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
    const niceTime = mockNow.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit'
    });
    return `<div class="ship-week__test-badge" role="note">
      ⚠️ TEST MODE — pretending it's ${escapeHtml(niceDate)} at ${escapeHtml(niceTime)}.
      Clear "Mock now" in Theme Editor before publishing.
    </div>`;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Test-only export. Strictly Node-gated so it cannot interact with any
  // Shopify storefront global. The triple guard requires (a) a CommonJS
  // `module` object, (b) a writable `module.exports`, AND (c) Node.js
  // process metadata. All three together exist only when this file is
  // require()'d by the Vitest test runner. Under <script defer> in a
  // Shopify theme, `process` is undefined — branch is skipped, no globals
  // mutated, no side effects.
  if (
    typeof module === 'object' &&
    module !== null &&
    typeof module.exports === 'object' &&
    typeof process !== 'undefined' &&
    process.versions != null &&
    process.versions.node != null
  ) {
    module.exports = {
      weekMondayOf,
      weekFridayOf,
      ordinalSuffix,
      formatShipWeekLabel,
      buildPillDates,
      computeArrivalForZip,
    };
  }
})();
